// AdminV2Service removed - now using generated endpoints directly
import type {
  AdminEligibilityListParams,
  AdminEligibilityListResponse,
  AdminEligibilityItem,
} from '../types/eligibility';
import type { AnyModule, UpdateModulePayload } from '@components/Admin/modules/module.types';
import type {
  AnyTemplate,
  PaginatedTemplatesResponse,
  TemplateListParams,
} from '@components/Admin/templates/template.types';
import { encountersApi } from '@features/encounters/store/api';
import { enhancedApi } from '@store/generated/admin';
import type {
  AdminNoteProcessingSessionDetailResponse,
  BundleAssignmentResponse,
  BundleListResponse,
  AdminProviderLookupResponse,
  AdminProviderLookupUser,
  AdminProviderResponse,
  ListResponseAdminAppointmentResponse,
  ListResponseAdminEncounterResponse,
  ListResponseAdminNoteListItem,
  ListResponseAdminTranscriptionSessionResponse,
  OverrideOnboardingStatusResponse,
} from '@store/generated/admin';
import type {
  ConflictItemSchema,
  ConflictListResponse,
  SessionPatientMatchSchema,
  SessionPatientMatchesListResponse,
  StagedResourceSchema,
} from '@store/generated/data-migration';

// A browsed staged row plus its selection state (GDN-2297). The generated
// StagedResourceSchema predates these fields, so the hand-authored bridge below
// carries them directly — no codegen regen / next-deploy wait, same as the
// verdict/search params on browseStagedResources.
export type BrowsedStagedResource = StagedResourceSchema & {
  is_selected?: boolean;
  selected_by?: string | null;
  selected_at?: string | null;
  // Org-review flag (GDN-2300): an orthogonal, persisted, toggleable flag that
  // replaces the old `escalate` commit-time conflict decision. Same shape as
  // the selection fields above — hand-carried on the bridge until the generated
  // StagedResourceSchema picks them up. `org_review_by` is a user id or the
  // literal "agent" (the MCP agent flags rows too, like selection).
  org_review_needed?: boolean;
  org_review_by?: string | null;
  org_review_at?: string | null;
};

// Data migration types (temporary until OpenAPI is updated)
export interface ImportSessionResponse {
  session_id: string;
  organization_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'in_progress';
  total_patients: number;
  patient_count?: number;
  patients?: MigratedPatient[];
  new_patients?: MigratedPatient[] | number;
  failed_patients?: number;
  progress_percentage?: number;
  error_messages?:
    | Array<{
        error_type?: string;
        message: string;
        timestamp?: string;
      }>
    | string[];
  created_at: string;
  updated_at: string;
  completed_at?: string;
  file_type?: string;
  file_name?: string;
  imported_patients?: number;
  new_patients_count?: number;
  updated_patients?: number;
  updated_patients_count?: number;
}

export interface MigratedPatient {
  patient: {
    patient_id: string;
    first_name?: string;
    last_name?: string;
    date_of_birth?: string;
    dob?: string; // Alternative date field
    email?: string;
    phone?: string;
    gender?: string;
    identifier?: string;
  };
  migration_metadata?: {
    import_status: 'pending' | 'success' | 'failed' | 'created' | 'updated';
    error_message?: string;
  };
  clinical_data?: {
    diagnosis?: string[];
    diagnoses?: string[]; // Alternative field name
    medications?: string[];
    allergies?: string[];
  };
}

export interface ImportUploadResponse {
  session_id: string;
  message: string;
}

// Presigned raw-upload URLs (GDN-2196). Hand-authored: this BE endpoint isn't in
// the deployed OpenAPI yet, so codegen can't generate it. The browser asks for a
// set of presigned S3 PUT URLs, then uploads the file bytes DIRECTLY to S3 — the
// app server never buffers the payload, which is what timed out on large files.
export interface PresignRawUploadFileRequest {
  /** Filename, may be a relative path (folder structure is preserved). */
  filename: string;
}

export interface PresignRawUploadsRequest {
  organization_id: string;
  files: PresignRawUploadFileRequest[];
  /** Append to an existing batch; omit to start a new one. */
  upload_id?: string;
}

export interface PresignedRawUploadFile {
  /** Full S3 key of the object. */
  key: string;
  /** Echoes the requested filename / relative path. */
  filename: string;
  content_type: string;
  /** Presigned S3 PUT URL (carries its own auth — do NOT add an auth header). */
  url: string;
  /** Headers to send EXACTLY as-is with the PUT (Content-Type + SSE). */
  headers: Record<string, string>;
}

export interface PresignRawUploadsResponse {
  upload_id: string;
  bucket: string;
  files: PresignedRawUploadFile[];
  /** Relative paths skipped because they already exist in the batch. */
  skipped: string[];
}

export interface ImportSessionsListResponse {
  sessions: ImportSessionResponse[];
  total: number;
}

export interface ExportImportResultsResponse {
  patients: MigratedPatient[];
  patient_count?: number;
}

// GDN-2198: the staged-resources review view must see EVERY conflict / patient
// match in a session, not just the first display window. The BE list endpoints
// cap `per_page` at 1000, so these responses are the merged result of paging
// each endpoint to completion (see the queryFn endpoints below).
export interface AllClassifyConflictsResponse {
  items: ConflictItemSchema[];
  total: number;
}

export interface AllSessionPatientMatchesResponse {
  items: SessionPatientMatchSchema[];
  total: number;
}

// Update detail response types to match actual API responses
interface TranscriptDetailsResponse {
  transcription: any;
  provider: AdminProviderLookupUser;
  notes: any[];
  encounter: any;
  appointment: any;
}

interface EncounterDetailsResponse {
  encounter: any;
  provider: AdminProviderLookupUser;
  notes: any[];
  transcriptions: any[];
  appointment: any;
}

interface AppointmentDetailsResponse {
  appointment: any;
  provider: AdminProviderLookupUser;
  encounter: any;
  transcriptions: any[];
  notes: any[];
}

interface NoteProcessingSessionResponse {
  note_processing_session_id: string;
  provider_id: string;
  encounter_id: string;
  transcription_session_id: string;
  transcription_session_ids: string[];
  note_id?: string;
  created_at: string;
  updated_at: string;
  status: string;
}

interface ListResponseNoteProcessingSession {
  items: NoteProcessingSessionResponse[];
  total: number;
  page: number;
  per_page: number;
}

interface NoteProcessingSessionDetailsResponse {
  note_processing_session: AdminNoteProcessingSessionDetailResponse;
  provider: AdminProviderLookupUser;
  encounter: any;
  note: any;
  transcripts: any[];
}

// Prospect/Sales types
export interface ProspectResponse {
  prospect_segment_id: number;
  organization: string;
  name: string;
  email: string;
  job_title?: string;
  phone_number?: string;
  requesting_user_id?: string;
  source?: string;
  // Enriched fields
  specialty_enriched?: string;
  location?: string[];
  zipcode?: string;
  mso_count?: number;
  mso_platforms?: string[];
  services_modalities?: string[];
  insurance_types?: string[];
  insurance_carriers?: string[];
  population_focus?: string[];
  clinical_focus?: string[];
  owns_solo_practice?: boolean;
  in_group_practice?: boolean;
  owns_group_practice?: boolean;
  group_practice_website?: string[];
  colleague_count?: number;
  b2c_vs_b2b?: string;
  created_at: string;
  updated_at: string;
}

export interface ProspectsListResponse {
  prospects: ProspectResponse[];
  total_count: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface UploadCsvProspectsResponse {
  status: string;
  total_requested: number;
  errors?: string[];
  csv_rows_processed: number;
}

// Streaming threads types
export interface StreamingThreadListItem {
  thread_id: string;
  user_id: string;
  user_name?: string;
  org_id: string;
  initial_message: string;
  first_message_at: string;
  last_message_at: string;
  event_count: number;
  note_id?: string;
  encounter_id?: string;
  template_id?: string;
  template_name?: string;
}

export interface StreamingThreadsListResponse {
  threads: StreamingThreadListItem[];
}

export interface StreamingThreadMessage {
  created_at: string;
  event_type: string;
  payload: Record<string, unknown>;
}

export interface StreamingThreadDetailResponse {
  thread_id: string;
  total_events: number;
  messages: StreamingThreadMessage[];
  note_id?: string;
  encounter_id?: string;
  user_id?: string;
  user_name?: string;
  template_id?: string;
  template_name?: string;
}

// DoseSpot onboarding status
export type DosespotOnboardingStatus = 'idp_needed' | 'tfa_needed' | 'completed';

// DoseSpot patient types
export interface DosespotPatientData {
  Prefix?: string;
  FirstName: string;
  MiddleName?: string;
  LastName: string;
  Suffix?: string;
  DateOfBirth: string;
  Gender: string;
  Email?: string;
  Address1: string;
  Address2?: string;
  City: string;
  State: string;
  ZipCode: string;
  PhoneAdditional1?: string;
  PhoneAdditional2?: string;
  PhoneAdditionalType1?: string;
  PhoneAdditionalType2?: string;
  PrimaryPhone: string;
  PrimaryPhoneType: string;
  Weight?: number;
  WeightMetric?: string;
  Height?: number;
  HeightMetric?: string;
  NonDoseSpotMedicalRecordNumber?: string;
  Active: boolean;
  Encounter?: string;
  IsHospice?: boolean;
  PatientId?: number;
}

export interface DosespotSearchResponse {
  Items: DosespotPatientData[];
  Result: { ResultCode: string; ResultDescription: string };
}

export interface DosespotPatientResponse {
  Item: DosespotPatientData;
  Result: { ResultCode: string; ResultDescription: string };
}

export interface DosespotUpdateResponse {
  Id: number;
  Result: { ResultCode: string; ResultDescription: string };
}

/**
 * AdminV2 API - Using auto-generated OpenAPI endpoints
 * This replaces the manual implementation with generated endpoints
 */

// Define tag types for cache management
const ADMIN_V2_TAG_TYPES = [
  'Provider',
  'Note',
  'Transcription',
  'Encounter',
  'Appointment',
  'Group',
  'NoteProcessingSession',
  'GoogleCalendar',
  'DataMigration',
  'ImportSelection',
  'ImportOrgReview',
  'VoiceEnrollment',
  'StatsigGate',
  'Prospect',
  'AdminEligibility',
  'AdminEncounterType',
  'DosespotPatient',
  'CortexPolicy',
  'SystemNotificationConfig',
] as const;

// Create enhanced API with proper typing for transforms
export const adminV2Api = enhancedApi
  .enhanceEndpoints({
    addTagTypes: ADMIN_V2_TAG_TYPES,
    endpoints: {
      getV2AdminProvidersByProviderIdEncounterTypes: {
        providesTags: result =>
          result
            ? [
                ...result.items.map(({ encounter_type_id }) => ({
                  type: 'AdminEncounterType' as const,
                  id: encounter_type_id,
                })),
                { type: 'AdminEncounterType' as const, id: 'LIST' },
              ]
            : [{ type: 'AdminEncounterType' as const, id: 'LIST' }],
      },
      postV2AdminProvidersByProviderIdEncounterTypes: {
        invalidatesTags: [{ type: 'AdminEncounterType' as const, id: 'LIST' }],
      },
      patchV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeId: {
        invalidatesTags: (_result, _error, { encounterTypeId }) => [
          { type: 'AdminEncounterType' as const, id: encounterTypeId },
          { type: 'AdminEncounterType' as const, id: 'LIST' },
        ],
      },
      deleteV2AdminProvidersByProviderIdEncounterTypesAndEncounterTypeId: {
        invalidatesTags: (_result, _error, { encounterTypeId }) => [
          { type: 'AdminEncounterType' as const, id: encounterTypeId },
          { type: 'AdminEncounterType' as const, id: 'LIST' },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Override provider lookup to return the user object directly
      getProviderInfo: builder.query<AdminProviderLookupUser, { providerId: string }>({
        query: ({ providerId }) => `/v2/admin/providers/${providerId}`,
        transformResponse: (
          response: AdminProviderLookupResponse,
        ): AdminProviderLookupUser => response.user,
        providesTags: (result, error, { providerId }) => [
          { type: 'Provider', id: providerId },
          { type: 'Provider', id: `info_${providerId}` },
          'Provider',
        ],
      }),

      // Override the auto-generated PDF export to return a Blob instead of unknown
      exportAgentPdf: builder.mutation<Blob, { title: string; markdown: string }>({
        query: ({ title, markdown }) => ({
          url: `/v2/admin/agent/export/pdf`,
          method: 'POST',
          body: { title, markdown },
          responseHandler: async (response: Response) => response.blob(),
        }),
      }),

      getBundlesIncludingHidden: builder.query<BundleListResponse, void>({
        query: () => ({
          url: '/v2/admin/bundles',
          params: { include_hidden: true },
        }),
      }),

      cancelBundleAssignment: builder.mutation<
        BundleAssignmentResponse,
        {
          assignmentId: string;
          organizationId: string;
          cancelImmediately: boolean;
          reason?: string;
        }
      >({
        query: ({ assignmentId, organizationId, cancelImmediately, reason }) => ({
          url: `/v2/admin/bundle-assignments/${assignmentId}/cancel`,
          method: 'POST',
          body: {
            organization_id: organizationId,
            cancel_immediately: cancelImmediately,
            reason,
          },
        }),
      }),

      // Override the auto-generated uploadImportFile to properly handle file uploads
      uploadImportFile: builder.mutation<
        ImportUploadResponse,
        { file: File; organizationId: string }
      >({
        query: ({ file, organizationId }) => {
          const formData = new FormData();
          formData.append('file', file, file.name);
          formData.append('organization_id', organizationId);

          return {
            url: `/v2/admin/data-migration/upload`,
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: ['DataMigration'],
      }),

      // GDN-2198: fetch EVERY conflict in a session, not just the first display
      // window. The staged-resources review view must surface every conflict so
      // the operator can resolve them all — an undecided conflict is the only
      // thing that blocks a commit, and the BE gate independently rejects the
      // commit if any conflict lacks a disposition. The generated /conflicts
      // endpoint caps `per_page` at 1000, so we page it to completion and merge.
      listAllClassifyConflicts: builder.query<
        AllClassifyConflictsResponse,
        { organizationId: string; sessionId: string }
      >({
        queryFn: async (
          { organizationId, sessionId },
          _api,
          _extraOptions,
          baseQuery,
        ) => {
          const PER_PAGE = 1000;
          const MAX_PAGES = 50; // safety backstop (≤ 50k conflicts)
          const items: ConflictItemSchema[] = [];
          let total = 0;
          for (let page = 1; page <= MAX_PAGES; page += 1) {
            const res = await baseQuery({
              url: `/v2/admin/data-migration/sessions/${sessionId}/conflicts`,
              params: {
                page,
                per_page: PER_PAGE,
                organization_id: organizationId,
              },
            });
            if (res.error) return { error: res.error };
            const body = (res.data ?? {}) as ConflictListResponse;
            const batch = body.items ?? [];
            items.push(...batch);
            total = body.total ?? items.length;
            if (batch.length < PER_PAGE || items.length >= total) break;
          }
          return { data: { items, total } };
        },
      }),

      // GDN-2198: fetch EVERY staged-patient match to completion (same 1000 cap
      // as /conflicts), so a large batch never silently hides an ambiguous
      // match — which would otherwise block the commit with no way to resolve it.
      listAllSessionPatientMatches: builder.query<
        AllSessionPatientMatchesResponse,
        { organizationId: string; sessionId: string }
      >({
        queryFn: async (
          { organizationId, sessionId },
          _api,
          _extraOptions,
          baseQuery,
        ) => {
          const PER_PAGE = 1000;
          const MAX_PAGES = 50; // safety backstop (≤ 50k staged patients)
          const items: SessionPatientMatchSchema[] = [];
          let total = 0;
          for (let page = 1; page <= MAX_PAGES; page += 1) {
            const res = await baseQuery({
              url: `/v2/admin/data-migration/sessions/${sessionId}/patients/matches`,
              params: {
                page,
                per_page: PER_PAGE,
                organization_id: organizationId,
              },
            });
            if (res.error) return { error: res.error };
            const body = (res.data ?? {}) as SessionPatientMatchesListResponse;
            const batch = body.items ?? [];
            items.push(...batch);
            total = body.total ?? items.length;
            if (batch.length < PER_PAGE || items.length >= total) break;
          }
          return { data: { items, total } };
        },
      }),

      // Presigned raw-upload URLs (GDN-2196). Returns one presigned S3 PUT URL
      // per file; the browser then PUTs the bytes directly to S3, bypassing the
      // app server. Hand-authored because the endpoint isn't in the deployed
      // OpenAPI yet. No tags are invalidated here — the objects don't exist in
      // S3 until the subsequent direct PUTs finish, so the raw-uploads list is
      // refreshed by the caller once the uploads complete.
      presignRawUploads: builder.mutation<
        PresignRawUploadsResponse,
        PresignRawUploadsRequest
      >({
        query: body => ({
          url: `/v2/admin/data-migration/raw-upload-urls`,
          method: 'POST',
          body,
        }),
      }),

      // Re-run the classify pass for a session (GDN-2244) — async and
      // idempotent; for sessions whose persisted verdicts predate a classify
      // change. Hand-authored until the deployed OpenAPI catches up.
      getStagedResourceDocument: builder.query<
        {
          key: string;
          content_type: string;
          size: number;
          text: string;
          truncated: boolean;
        },
        {
          organizationId: string;
          sessionId: string;
          importResourceId: string;
        }
      >({
        query: ({ organizationId, sessionId, importResourceId }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/resources/${importResourceId}/document`,
          params: { organization_id: organizationId },
        }),
      }),
      requestClassify: builder.mutation<
        { import_session_id: string; enqueued: boolean },
        { organizationId: string; sessionId: string }
      >({
        query: ({ organizationId, sessionId }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/classify`,
          method: 'POST',
          body: { organization_id: organizationId },
        }),
      }),

      // Server-driven browse page for the review browser (GDN-2244, GDN-2287):
      // the generated listStagedResources predates the `search`/`verdict`
      // params, so this bridge carries type/status/search/verdict/page directly
      // to the endpoint. `verdict` filters by classify verdict BE-side
      // pre-pagination (GDN-2287) so the FE never merges client-side.
      browseStagedResources: builder.query<
        {
          items: BrowsedStagedResource[];
          total: number;
          page: number;
          per_page: number;
        },
        {
          organizationId: string;
          sessionId: string;
          page: number;
          perPage: number;
          resourceType?: string;
          status?: string;
          search?: string;
          verdict?: string;
          selected?: boolean;
          orgReviewNeeded?: boolean;
        }
      >({
        query: ({
          organizationId,
          sessionId,
          page,
          perPage,
          resourceType,
          status,
          search,
          verdict,
          selected,
          orgReviewNeeded,
        }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/resources`,
          params: {
            organization_id: organizationId,
            page,
            per_page: perPage,
            ...(resourceType ? { resource_type: resourceType } : {}),
            ...(status ? { status } : {}),
            ...(search ? { search } : {}),
            ...(verdict ? { verdict } : {}),
            ...(selected !== undefined ? { selected } : {}),
            // Org-review filter (GDN-2300) rides the same server browse, parallel
            // to `selected`; the BE returns only flagged/unflagged rows.
            ...(orgReviewNeeded !== undefined
              ? { org_review_needed: orgReviewNeeded }
              : {}),
          },
        }),
        // Selection mutations invalidate this so is_selected and the selected
        // counts refresh across every cached page/summary (GDN-2297); the
        // org-review toggle (GDN-2300) invalidates it the same way so
        // org_review_needed and the flagged counts stay fresh.
        providesTags: ['ImportSelection', 'ImportOrgReview'],
      }),

      // GDN-2297: add/remove staged rows from the session's shared selection
      // working set (the same set the agent drives via MCP). `selected` toggles
      // membership; provenance is stamped BE-side from the authenticated user.
      selectImportRows: builder.mutation<
        { updated: number; selected_total: number },
        {
          organizationId: string;
          sessionId: string;
          importResourceIds: string[];
          selected: boolean;
        }
      >({
        query: ({ organizationId, sessionId, importResourceIds, selected }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/resources/select`,
          method: 'POST',
          body: {
            organization_id: organizationId,
            import_resource_ids: importResourceIds,
            selected,
          },
        }),
        invalidatesTags: ['ImportSelection'],
      }),

      clearImportSelection: builder.mutation<
        { updated: number; selected_total: number },
        { organizationId: string; sessionId: string }
      >({
        query: ({ organizationId, sessionId }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/resources/selection/clear`,
          method: 'POST',
          body: { organization_id: organizationId },
        }),
        invalidatesTags: ['ImportSelection'],
      }),

      // GDN-2300: flag/unflag staged rows for org-level review — an orthogonal,
      // persisted, toggleable flag that replaces the old `escalate` commit-time
      // decision. `needed` toggles membership (true flags, false unflags);
      // provenance is stamped BE-side from the authenticated user (or "agent"
      // when the MCP agent flags a row). Mirrors selectImportRows (GDN-2297).
      setOrgReview: builder.mutation<
        { updated: number; org_review_total: number },
        {
          organizationId: string;
          sessionId: string;
          importResourceIds: string[];
          needed: boolean;
        }
      >({
        query: ({ organizationId, sessionId, importResourceIds, needed }) => ({
          url: `/v2/admin/data-migration/sessions/${sessionId}/resources/org-review`,
          method: 'POST',
          body: {
            organization_id: organizationId,
            import_resource_ids: importResourceIds,
            needed,
          },
        }),
        invalidatesTags: ['ImportOrgReview'],
      }),
    }),
    overrideExisting: true,
  })
  .enhanceEndpoints({
    endpoints: {
      getV2AdminOrganizationsByOrganizationIdProviders: {
        providesTags: (result, error, { organizationId }) => [
          { type: 'Provider', id: `org_${organizationId}` },
        ],
      },

      // Notes endpoints
      getV2AdminProvidersByProviderIdNotes: {
        providesTags: (result, error, { providerId }) => [{ type: 'Note', id: providerId }],
      },

      getV2AdminNotesByNoteId: {
        providesTags: (result, error, { noteId }) => [{ type: 'Note', id: noteId }],
      },

      // Transcriptions endpoints
      getV2AdminProvidersByProviderIdTranscriptions: {
        providesTags: (result, error, { providerId }) => [
          { type: 'Transcription', id: providerId },
        ],
      },

      getV2AdminTranscriptionsByTranscriptionSessionId: {
        providesTags: (result, error, { transcriptionSessionId }) => [
          { type: 'Transcription', id: transcriptionSessionId },
        ],
      },

      // Encounters endpoints
      getV2AdminProvidersByProviderIdEncounters: {
        providesTags: (result, error, { providerId }) => [{ type: 'Encounter', id: providerId }],
      },

      getV2AdminEncountersByEncounterId: {
        providesTags: (result, error, { encounterId }) => [{ type: 'Encounter', id: encounterId }],
      },

      // Appointments endpoints
      getV2AdminProvidersByProviderIdAppointments: {
        providesTags: (result, error, { providerId }) => [{ type: 'Appointment', id: providerId }],
      },

      getV2AdminAppointmentsByAppointmentId: {
        providesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
        ],
      },

      // Voice Enrollment endpoints
      getV2AdminUsersByUserIdVoiceEnrollment: {
        providesTags: (result, error, { userId }) => [
          { type: 'VoiceEnrollment', id: userId },
          { type: 'Provider', id: userId },
        ],
      },

      deleteV2AdminUsersByUserIdVoiceEnrollment: {
        invalidatesTags: (result, error, { userId }) => [
          { type: 'VoiceEnrollment', id: userId },
          { type: 'Provider', id: userId },
        ],
      },

      // Statsig Gates endpoints
      getV2AdminApplicationConfigStatsigGates: {
        providesTags: ['StatsigGate'],
      },

      getV2AdminApplicationConfigStatsigUsersByUserIdGates: {
        providesTags: (result, error, { userId }) => [
          { type: 'StatsigGate', id: `user_${userId}` },
          'StatsigGate',
        ],
      },

      getV2AdminApplicationConfigStatsigGatesByGateIdOverrides: {
        providesTags: (result, error, { gateId }) => [{ type: 'StatsigGate', id: gateId }],
      },

      patchV2AdminApplicationConfigStatsigGatesByGateIdOverrides: {
        invalidatesTags: (result, error, { gateId }) => [
          { type: 'StatsigGate', id: gateId },
          'StatsigGate',
        ],
      },

      postV2AdminApplicationConfigStatsigGatesByGateIdOverridesRemove: {
        invalidatesTags: (result, error, { gateId }) => [
          { type: 'StatsigGate', id: gateId },
          'StatsigGate',
        ],
      },

      getV2AdminApplicationConfigStatsigSystemNotifications: {
        providesTags: ['SystemNotificationConfig'],
      },

      putV2AdminApplicationConfigStatsigSystemNotifications: {
        invalidatesTags: ['SystemNotificationConfig'],
      },

      // Org-scoped Statsig gate endpoints
      getV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGates: {
        providesTags: (result, error, { organizationId }) => [
          { type: 'StatsigGate', id: `org_${organizationId}` },
          'StatsigGate',
        ],
      },

      patchV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGatesAndGateIdOverrides: {
        invalidatesTags: (result, error, { organizationId, gateId }) => [
          { type: 'StatsigGate', id: gateId },
          { type: 'StatsigGate', id: `org_${organizationId}` },
          'StatsigGate',
        ],
      },

      postV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGatesAndGateIdOverridesRemove:
        {
          invalidatesTags: (result, error, { organizationId, gateId }) => [
            { type: 'StatsigGate', id: gateId },
            { type: 'StatsigGate', id: `org_${organizationId}` },
            'StatsigGate',
          ],
        },

      // Provider profile update endpoint
      patchV2AdminProvidersByProviderIdProfile: {
        invalidatesTags: (result, error, { providerId }) => [
          { type: 'Provider', id: providerId },
          { type: 'Provider', id: `info_${providerId}` },
          'Provider',
        ],
      },

      // Cortex Policies (GDN-1816). All mutations invalidate the LIST tag so
      // the list query refetches; per-id reads/writes also touch the row tag
      // so dialog re-opens after a save show fresh data without a hard reload.
      getV2AdminCortexPolicies: {
        providesTags: result => [
          { type: 'CortexPolicy', id: 'LIST' },
          ...(result?.items ?? []).map(p => ({
            type: 'CortexPolicy' as const,
            id: p.policy_id,
          })),
        ],
      },
      getV2AdminCortexPoliciesByPolicyId: {
        providesTags: (result, error, { policyId }) => [
          { type: 'CortexPolicy', id: policyId },
        ],
      },
      postV2AdminCortexPolicies: {
        invalidatesTags: [{ type: 'CortexPolicy', id: 'LIST' }],
      },
      patchV2AdminCortexPoliciesByPolicyId: {
        invalidatesTags: (result, error, { policyId }) => [
          { type: 'CortexPolicy', id: policyId },
          { type: 'CortexPolicy', id: 'LIST' },
        ],
      },
      postV2AdminCortexPoliciesByPolicyIdSupersede: {
        // The source row gains a "Superseded by" entry; the new draft lands
        // in the list. Invalidate both.
        invalidatesTags: (result, error, { policyId }) => [
          { type: 'CortexPolicy', id: policyId },
          { type: 'CortexPolicy', id: 'LIST' },
        ],
      },
      postV2AdminCortexPoliciesByPolicyIdPromote: {
        invalidatesTags: (result, error, { policyId }) => [
          { type: 'CortexPolicy', id: policyId },
          { type: 'CortexPolicy', id: 'LIST' },
        ],
      },
      postV2AdminCortexPoliciesByPolicyIdDeprecate: {
        invalidatesTags: (result, error, { policyId }) => [
          { type: 'CortexPolicy', id: policyId },
          { type: 'CortexPolicy', id: 'LIST' },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: builder => ({
      // Wrapper endpoints for list queries with parameters
      getProviderNotesWithParams: builder.query<
        ListResponseAdminNoteListItem,
        {
          providerId: string;
          page?: number;
          perPage?: number;
          encounterId?: string;
          transcriptId?: string;
          status?: string;
          template?: string;
          search?: string;
          sortBy?: string;
          sortOrder?: string;
        }
      >({
        query: ({
          providerId,
          page = 1,
          perPage = 5,
          encounterId,
          transcriptId,
          status,
          template,
          search,
          sortBy,
          sortOrder,
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
          };

          if (encounterId) params.encounter_id = encounterId;
          if (transcriptId) params.transcript_id = transcriptId;
          if (status) params.status = status;
          if (template) params.template = template;
          if (search) params.search = search;
          if (sortBy) params.sort_by = sortBy;
          if (sortOrder) params.sort_order = sortOrder;

          return {
            url: `/v2/admin/providers/${providerId}/notes`,
            params,
          };
        },
        providesTags: (result, error, { providerId }) => [{ type: 'Note', id: providerId }],
      }),

      getProviderTranscriptionsWithParams: builder.query<
        ListResponseAdminTranscriptionSessionResponse,
        {
          providerId: string;
          page?: number;
          perPage?: number;
          encounterId?: string;
          appointmentId?: string;
          status?: string;
          search?: string;
        }
      >({
        query: ({
          providerId,
          page = 1,
          perPage = 10,
          encounterId,
          appointmentId,
          status,
          search,
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
          };

          if (encounterId) params.encounter_id = encounterId;
          if (appointmentId) params.appointment_id = appointmentId;
          if (status) params.status = status;
          if (search) params.search = search;

          return {
            url: `/v2/admin/providers/${providerId}/transcriptions`,
            params,
          };
        },
        providesTags: (result, error, { providerId }) => [
          { type: 'Transcription', id: providerId },
        ],
      }),

      getProviderEncountersWithParams: builder.query<
        ListResponseAdminEncounterResponse,
        {
          providerId: string;
          page?: number;
          perPage?: number;
          appointmentId?: string;
          transcriptId?: string;
          status?: string;
          encounterType?: string;
          search?: string;
        }
      >({
        query: ({
          providerId,
          page = 1,
          perPage = 10,
          appointmentId,
          transcriptId,
          status,
          encounterType,
          search,
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
          };

          if (appointmentId) params.appointment_id = appointmentId;
          if (transcriptId) params.transcript_id = transcriptId;
          if (status) params.status = status;
          if (encounterType) params.encounter_type = encounterType;
          if (search) params.search = search;

          return {
            url: `/v2/admin/providers/${providerId}/encounters`,
            params,
          };
        },
        providesTags: (result, error, { providerId }) => [{ type: 'Encounter', id: providerId }],
      }),

      getProviderAppointmentsWithParams: builder.query<
        ListResponseAdminAppointmentResponse,
        {
          providerId: string;
          page?: number;
          perPage?: number;
          encounterId?: string;
          status?: string;
          appointmentType?: string;
          search?: string;
        }
      >({
        query: ({
          providerId,
          page = 1,
          perPage = 10,
          encounterId,
          status,
          appointmentType,
          search,
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
          };

          if (encounterId) params.encounter_id = encounterId;
          if (status) params.status = status;
          if (appointmentType) params.appointment_type = appointmentType;
          if (search) params.search = search;

          return {
            url: `/v2/admin/providers/${providerId}/appointments`,
            params,
          };
        },
        providesTags: (result, error, { providerId }) => [{ type: 'Appointment', id: providerId }],
      }),

      getProviderNoteProcessingSessionsWithParams: builder.query<
        ListResponseNoteProcessingSession,
        {
          providerId: string;
          page?: number;
          perPage?: number;
          encounterId?: string;
          transcriptionSessionId?: string;
          noteId?: string;
          status?: string;
          search?: string;
        }
      >({
        query: ({
          providerId,
          page = 1,
          perPage = 10,
          encounterId,
          transcriptionSessionId,
          noteId,
          status,
          search,
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
          };

          if (encounterId) params.encounter_id = encounterId;
          if (transcriptionSessionId) params.transcription_session_id = transcriptionSessionId;
          if (noteId) params.note_id = noteId;
          if (status) params.status = status;
          if (search) params.search = search;

          return {
            url: `/v2/admin/providers/${providerId}/note-processing-sessions`,
            params,
          };
        },
        providesTags: (result, error, { providerId }) => [
          { type: 'NoteProcessingSession', id: providerId },
        ],
      }),

      // Custom endpoints that aren't in the OpenAPI spec

      // Google Calendar admin endpoints
      getProviderGoogleSyncStatus: builder.query<
        {
          is_configured: boolean;
          calendar_id?: string | null;
          has_refresh_token: boolean;
          is_integrated: boolean;
          has_sync_token: boolean;
          notification_channel_id?: string | null;
          notification_channel_expiration?: number | null;
          last_message_number?: number | null;
          error?: string | null;
        },
        { providerId: string }
      >({
        query: ({ providerId }) => `/v2/admin/providers/${providerId}/google-sync/status`,
        providesTags: (result, error, { providerId }) => [
          { type: 'GoogleCalendar', id: providerId },
          { type: 'Provider', id: providerId },
        ],
      }),

      initiateProviderGoogleSync: builder.mutation<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any,
        { providerId: string }
      >({
        query: ({ providerId }) => ({
          url: `/v2/admin/providers/${providerId}/google-sync`,
          method: 'POST',
        }),
        invalidatesTags: (result, error, { providerId }) => [
          { type: 'GoogleCalendar', id: providerId },
        ],
      }),

      deleteProviderGoogleCalendarConfig: builder.mutation<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any,
        { providerId: string; resetIntegrationFlag?: boolean }
      >({
        query: ({ providerId, resetIntegrationFlag }) => ({
          url: `/v2/admin/providers/${providerId}/google-sync`,
          method: 'DELETE',
          params: resetIntegrationFlag ? { reset_integration_flag: 'true' } : undefined,
        }),
        invalidatesTags: (result, error, { providerId }) => [
          { type: 'GoogleCalendar', id: providerId },
          { type: 'Provider', id: providerId },
        ],
      }),

      // Get templates for a specific provider with pagination support
      getProviderTemplates: builder.query<
        PaginatedTemplatesResponse,
        { providerId: string } & TemplateListParams
      >({
        query: ({ providerId, page = 1, pageSize = 10, ...params }) => {
          const queryParams = new URLSearchParams();
          queryParams.append('filterByUserId', providerId);
          queryParams.append('page', String(page));
          queryParams.append('pageSize', String(pageSize));

          // Add optional parameters
          Object.entries(params).forEach(([key, value]) => {
            if (value !== undefined) {
              queryParams.append(key, String(value));
            }
          });

          return `/admin/templates?${queryParams.toString()}`;
        },
        providesTags: (result, error, { providerId }) => [{ type: 'Provider', id: providerId }],
      }),

      // Get single template details
      getTemplateDetails: builder.query<
        { data: AnyTemplate },
        { templateId: string; templateVersion: number; type: 'public' | 'user' }
      >({
        query: ({ templateId, templateVersion, type }) =>
          `/admin/templates/${templateId}/${templateVersion}?type=${type}`,
        providesTags: (result, error, { templateId }) => [{ type: 'Provider', id: templateId }],
      }),

      // Get single module details by filtering the list
      getModuleDetails: builder.query<AnyModule, { moduleId: string; type: 'public' | 'user' }>({
        query: ({ moduleId, type }) =>
          `/admin/modules?filterByType=${type}&filterByModuleId=${moduleId}`,
        transformResponse: (response: { data: AnyModule[] }) => {
          // Extract the first module from the filtered list
          if (response.data && response.data.length > 0) {
            return response.data[0];
          }
          throw new Error('Module not found');
        },
        providesTags: (result, error, { moduleId }) => [{ type: 'Provider', id: moduleId }],
      }),

      // Update an existing module
      updateModule: builder.mutation<
        AnyModule,
        {
          moduleId: string;
          moduleVersion: number;
          type: 'public' | 'user';
          data: UpdateModulePayload;
        }
      >({
        query: ({ moduleId, moduleVersion, type, data }) => ({
          url: `/admin/modules/update?module_id=${moduleId}&module_version=${moduleVersion}&type=${type}`,
          method: 'POST',
          body: data,
        }),
        invalidatesTags: (result, error, { moduleId }) => [{ type: 'Provider', id: moduleId }],
      }),

      // Search providers - uses the old /user/getUsers endpoint
      searchProviders: builder.query<AdminProviderResponse[], string>({
        query: searchQuery => ({
          url: '/user/getUsers',
          params: {
            page: '1',
            pageSize: '100',
            sortBy: '',
            sortOrder: '',
            searchQuery: searchQuery,
            featureFilter: '',
            cohortFilter: '',
          },
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transformResponse: (response: any) => {
          // Map old API format to new format
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const providers: AdminProviderResponse[] = response.users.map(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (user: any) => ({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              provider_id: user._id || user.user_id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              name: user.user_info?.name || user.name || 'Unknown Provider',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              email: user.user_info?.email || user.email || 'No email',
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              note_count: user.user_metrics?.notes_created || 0,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              last_login_time: user.last_login_timestamp
                ? // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  new Date(user.last_login_timestamp * 1000).toISOString()
                : // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                  user.last_login_time || new Date().toISOString(),
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              role: user.role,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              organization_id: user.organization_id,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              organization_name: user.organization_name,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              group: user.group,
            }),
          );
          return providers;
        },
        providesTags: ['Provider'],
      }),

      // Legacy getProvider endpoint - for backward compatibility
      getProvider: builder.query<AdminProviderResponse, number>({
        query: userId => ({
          url: '/user/getUsers',
          params: {
            page: '1',
            pageSize: '1',
            sortBy: '',
            sortOrder: 'asc',
            searchQuery: userId.toString(),
            featureFilter: '',
            cohortFilter: '',
          },
        }),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        transformResponse: (response: any, _meta, userId) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
          const user = response.users.find(
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (u: any) =>
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              u.id === userId ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              u._id === userId.toString() ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              u.userId === userId.toString() ||
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              u.user_id === userId.toString(),
          );

          if (!user) {
            throw new Error('User not found');
          }

          return {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            provider_id: user.user_id || user._id,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            name: user.name || user.user_info?.name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            email: user.email || user.user_info?.email,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            note_count: user.note_count || user.user_metrics?.notes_created || 0,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            last_login_time: user.last_activity || user.last_login_time || new Date().toISOString(),
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            role: user.role,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            organization_id: user.organization_id,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            organization_name: user.organization_name,
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
            group: user.group,
          };
        },
        providesTags: (result, error, id) => [{ type: 'Provider', id }],
      }),

      // Feature flags management endpoints
      getFeaturesList: builder.query<string[], void>({
        query: () => ({
          url: '/testsuite/getFeaturesList',
        }),
        transformResponse: (response: { features_list: string[] }) => response.features_list ?? [],
      }),

      updateProviderFeatureFlag: builder.mutation<
        void,
        { providerId: string; featureName: string; featureValue: boolean }
      >({
        query: ({ providerId, featureName, featureValue }) => ({
          url: '/user/updateFeatureFlagOnUser',
          method: 'POST',
          body: {
            target_user_id: providerId,
            feature_name: featureName,
            feature_value: featureValue,
          },
        }),
        invalidatesTags: (result, error, { providerId }) => [
          { type: 'Provider', id: providerId },
          { type: 'Provider', id: `info_${providerId}` },
        ],
      }),

      // Legacy endpoint - replaced by direct provider lookup
      getUserDetailsQuery: builder.query<
        {
          user: AdminProviderLookupUser;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          notes: any[];
          transcriptions: never[];
          encounters: never[];
          appointments: never[];
        },
        number
      >({
        queryFn: async (providerId: number, { dispatch }: { dispatch: any }): Promise<any> => {
          try {
            // Use new provider info endpoint
            const providerResult = await dispatch(
              adminV2Api.endpoints.getProviderInfo.initiate({
                providerId: providerId.toString(),
              }),
            ).unwrap();

            // Get notes using generated endpoint
            const notesResult = await dispatch(
              enhancedApi.endpoints.getV2AdminProvidersByProviderIdNotes.initiate({
                providerId: providerId.toString(),
              }),
            ).unwrap();

            // Use provider data directly (now returns AdminProviderLookupUser)
            const user = providerResult;

            return {
              data: {
                user,
                notes: notesResult.items || [],
                transcriptions: [],
                encounters: [],
                appointments: [],
              },
            };
          } catch (error) {
            return {
              error: {
                status: 'CUSTOM_ERROR',
                error: error instanceof Error ? error.message : 'Unknown error',
              },
            };
          }
        },
        providesTags: (result, error, id) => [{ type: 'Provider', id }],
      }),

      // Detail page endpoints - now using single comprehensive backend calls
      getNoteProcessingSessionDetails: builder.query<
        NoteProcessingSessionDetailsResponse,
        { sessionId: string }
      >({
        query: ({ sessionId }) => `/v2/admin/note-processing-sessions/${sessionId}`,
        providesTags: (result, error, { sessionId }) => [
          { type: 'NoteProcessingSession', id: sessionId },
        ],
      }),

      getTranscriptDetails: builder.query<TranscriptDetailsResponse, { transcriptionId: string }>({
        query: ({ transcriptionId }) => `/v2/admin/transcriptions/${transcriptionId}`,
        providesTags: (result, error, { transcriptionId }) => [
          { type: 'Transcription', id: transcriptionId },
        ],
      }),

      getEncounterDetails: builder.query<EncounterDetailsResponse, { encounterId: string }>({
        query: ({ encounterId }) => `/v2/admin/encounters/${encounterId}`,
        providesTags: (result, error, { encounterId }) => [{ type: 'Encounter', id: encounterId }],
      }),

      getAppointmentDetails: builder.query<AppointmentDetailsResponse, { appointmentId: string }>({
        query: ({ appointmentId }) => `/v2/admin/appointments/${appointmentId}`,
        providesTags: (result, error, { appointmentId }) => [
          { type: 'Appointment', id: appointmentId },
        ],
      }),

      getNoteDetails: builder.query<any, { noteId: string }>({
        query: ({ noteId }) => `/v2/admin/notes/${noteId}`,
        providesTags: (result, error, { noteId }) => [{ type: 'Note', id: noteId }],
      }),

      // Encounter telemetry analytics endpoint (accepts either encounterId or noteId)
      getEncounterTelemetryAnalytics: builder.query<
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        any,
        { encounterId?: string; noteId?: string }
      >({
        query: ({ encounterId, noteId }) => {
          const params: Record<string, string> = {};
          if (encounterId) {
            params.encounter_id = encounterId;
          }
          if (noteId) {
            params.note_id = noteId;
          }
          return {
            url: `/v2/admin/encounters/telemetry/analytics`,
            params,
          };
        },
        providesTags: (result, error, { encounterId, noteId }) => {
          const tags: Array<{ type: (typeof ADMIN_V2_TAG_TYPES)[number]; id: string }> = [];
          if (encounterId) tags.push({ type: 'Encounter' as const, id: encounterId });
          if (noteId) tags.push({ type: 'Note' as const, id: noteId });
          return tags;
        },
      }),

      // Streaming threads endpoints
      getRecentStreamingThreads: builder.query<
        StreamingThreadsListResponse,
        { limit?: number; workflowName?: string; userId?: string }
      >({
        query: ({ limit = 10, workflowName, userId }) => ({
          url: `/v2/admin/streaming-threads/recent`,
          params: { limit, workflow_name: workflowName, user_id: userId },
        }),
      }),

      getStreamingThreadDetail: builder.query<StreamingThreadDetailResponse, { threadId: string }>({
        query: ({ threadId }) => `/v2/admin/streaming-threads/${threadId}`,
      }),

      // Prospects (Sales Data) endpoints
      getProspects: builder.query<
        ProspectsListResponse,
        {
          page?: number;
          pageSize?: number;
          organization?: string;
          email?: string;
          enriched?: boolean;
          sortBy?: string;
          sortOrder?: 'asc' | 'desc';
        }
      >({
        query: ({ page = 1, pageSize = 20, organization, email, enriched, sortBy, sortOrder }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            page_size: pageSize.toString(),
          };
          if (organization) params.organization = organization;
          if (email) params.email = email;
          if (enriched !== undefined) params.enriched = enriched.toString();
          if (sortBy) params.sort_by = sortBy;
          if (sortOrder) params.sort_order = sortOrder;
          return { url: '/v2/admin/prospects', params };
        },
        providesTags: ['Prospect'],
      }),

      getProspectById: builder.query<ProspectResponse, { prospectSegmentId: string }>({
        query: ({ prospectSegmentId }) => `/v2/admin/prospects/${prospectSegmentId}`,
        providesTags: (result, error, { prospectSegmentId }) => [
          { type: 'Prospect', id: prospectSegmentId },
        ],
      }),

      uploadCsvProspects: builder.mutation<UploadCsvProspectsResponse, { file: File }>({
        query: ({ file }) => {
          const formData = new FormData();
          formData.append('file', file, file.name);
          return {
            url: '/v2/admin/upload-csv-prospects',
            method: 'POST',
            body: formData,
          };
        },
        invalidatesTags: ['Prospect'],
      }),

      // Eligibility Workqueue endpoints
      getAdminEligibilityList: builder.query<
        AdminEligibilityListResponse,
        AdminEligibilityListParams
      >({
        query: ({
          page = 1,
          perPage = 20,
          nextActionOwner,
          status,
          nextAction,
          search,
          sortBy = 'created_at',
          sortOrder = 'desc',
        }) => {
          const params: Record<string, string> = {
            page: page.toString(),
            per_page: perPage.toString(),
            sort_by: sortBy,
            sort_order: sortOrder,
          };
          if (nextActionOwner) params.next_action_owner = nextActionOwner;
          if (status) params.status = status;
          if (nextAction) params.next_action = nextAction;
          if (search) params.search = search;
          return { url: '/v2/admin/eligibility', params };
        },
        providesTags: result =>
          result
            ? [
                ...result.items.map(item => ({
                  type: 'AdminEligibility' as const,
                  id: item.id,
                })),
                { type: 'AdminEligibility', id: 'LIST' },
              ]
            : [{ type: 'AdminEligibility', id: 'LIST' }],
      }),

      adminOverrideEligibility: builder.mutation<
        AdminEligibilityItem,
        { eligibilityId: string; body: Record<string, unknown> }
      >({
        query: ({ eligibilityId, body }) => ({
          url: `/v2/admin/eligibility/${eligibilityId}/override`,
          method: 'POST',
          body,
        }),
        invalidatesTags: (_result, _error, { eligibilityId }) => [
          { type: 'AdminEligibility', id: eligibilityId },
          { type: 'AdminEligibility', id: 'LIST' },
        ],
      }),

      // DoseSpot patient management
      searchDosespotPatients: builder.query<
        DosespotSearchResponse,
        {
          organizationId: string;
          firstname?: string;
          lastname?: string;
          dob?: string;
          page?: number;
        }
      >({
        query: ({ organizationId, ...params }) => ({
          url: `/v2/admin/organizations/${organizationId}/eprescriptions/patients/search`,
          params,
        }),
      }),

      getDosespotPatient: builder.query<
        DosespotPatientResponse,
        { organizationId: string; dosespotPatientId: string }
      >({
        query: ({ organizationId, dosespotPatientId }) =>
          `/v2/admin/organizations/${organizationId}/eprescriptions/patients/${dosespotPatientId}`,
        providesTags: (_result, _error, { dosespotPatientId }) => [
          { type: 'DosespotPatient' as const, id: dosespotPatientId },
        ],
      }),

      updateDosespotPatient: builder.mutation<
        DosespotUpdateResponse,
        { organizationId: string; dosespotPatientId: string; body: DosespotPatientData }
      >({
        query: ({ organizationId, dosespotPatientId, body }) => ({
          url: `/v2/admin/organizations/${organizationId}/eprescriptions/patients/${dosespotPatientId}`,
          method: 'PUT',
          body,
        }),
        invalidatesTags: (_result, _error, { dosespotPatientId }) => [
          { type: 'DosespotPatient' as const, id: dosespotPatientId },
        ],
      }),

      // DoseSpot onboarding status override
      updateDosespotOnboardingStatus: builder.mutation<
        OverrideOnboardingStatusResponse,
        { userId: string; onboardingStatus: DosespotOnboardingStatus }
      >({
        query: ({ userId, onboardingStatus }) => ({
          url: `/v2/admin/eprescriptions/onboarding/users/${userId}/status`,
          method: 'PUT',
          body: { status: onboardingStatus },
        }),
        invalidatesTags: (_result, _error, { userId }) => [
          { type: 'Provider', id: userId },
          { type: 'Provider', id: `info_${userId}` },
        ],
      }),
    }),
    overrideExisting: false,
  });

// Export hooks with user-friendly names
export const {
  // Provider endpoints
  useGetV2AdminProvidersByProviderIdQuery: useGetProviderByIdQuery,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery: useGetOrganizationProvidersQuery,

  // Notes endpoints - use wrapper for list queries
  useGetProviderNotesWithParamsQuery: useGetProviderNotesQuery,
  useGetV2AdminNotesByNoteIdQuery: useGetNoteDetailsGeneratedQuery,

  // Transcriptions endpoints - use wrapper for list queries
  useGetProviderTranscriptionsWithParamsQuery: useGetProviderTranscriptionsQuery,
  useGetV2AdminTranscriptionsByTranscriptionSessionIdQuery: useGetTranscriptDetailsGeneratedQuery,

  // Encounters endpoints - use wrapper for list queries
  useGetProviderEncountersWithParamsQuery: useGetProviderEncountersQuery,
  useGetV2AdminEncountersByEncounterIdQuery: useGetEncounterDetailsGeneratedQuery,

  // Appointments endpoints - use wrapper for list queries
  useGetProviderAppointmentsWithParamsQuery: useGetProviderAppointmentsQuery,
  useGetV2AdminAppointmentsByAppointmentIdQuery: useGetAppointmentDetailsGeneratedQuery,

  // Note Processing Sessions endpoints
  useGetProviderNoteProcessingSessionsWithParamsQuery: useGetProviderNoteProcessingSessionsQuery,
  useGetNoteProcessingSessionDetailsQuery,

  // Detail page endpoints
  useGetTranscriptDetailsQuery,
  useGetEncounterDetailsQuery,
  useGetAppointmentDetailsQuery,
  useGetNoteDetailsQuery,

  // Unified admin search
  useGetV2AdminSearchQuery: useAdminSearchQuery,

  // Custom endpoints
  useSearchProvidersQuery,
  useGetProviderQuery,
  useGetUserDetailsQueryQuery,

  // Google Calendar admin endpoints
  useGetProviderGoogleSyncStatusQuery,
  useInitiateProviderGoogleSyncMutation,
  useDeleteProviderGoogleCalendarConfigMutation,

  // Templates endpoints
  useGetProviderTemplatesQuery,
  useGetTemplateDetailsQuery,

  // Module endpoints
  useGetModuleDetailsQuery,
  useUpdateModuleMutation,
} = adminV2Api;

// Encounter telemetry hooks (from encounters API)
export const {
  useGetV2ProviderEncountersTelemetryByEncounterIdQuery: useGetEncounterTelemetryQuery,
} = encountersApi;

// Encounter telemetry analytics hook (custom endpoint)
export const { useGetEncounterTelemetryAnalyticsQuery } = adminV2Api;

// Create alias for backward compatibility
export const useGetUserDetailsQuery = adminV2Api.useGetUserDetailsQueryQuery;

// Additional exports
export const useGetProviderInfoQuery = adminV2Api.useGetProviderInfoQuery;

// Export organizations API hooks directly from the generated API
export const {
  useGetV2AdminOrganizationsQuery,
  useGetV2AdminOrganizationsByOrganizationIdQuery,
  useGetV2AdminOrganizationsByOrganizationIdProvidersQuery,
  usePostV2AdminOrganizationsByOrganizationIdMembersMutation,
  useDeleteV2AdminOrganizationsByOrganizationIdMembersAndUserIdMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdMembersAndUserIdReactivateMutation,
  usePatchV2AdminOrganizationsByOrganizationIdMembersAndUserIdRolesMutation,
  usePostV2AdminOrganizationsByOrganizationIdSyncSubscriptionMutation,
  usePostV2AdminOrganizationsByOrganizationIdExtendTrialMutation,
  // Feature Explorer hooks
  useGetV2AdminApplicationConfigFeaturesQuery: useGetAvailableFeaturesQuery,
  useGetV2AdminApplicationConfigFeaturesByFeatureNameStatsQuery: useGetFeatureStatsQuery,
  useGetV2AdminApplicationConfigFeaturesByFeatureNameByOrganizationQuery:
    useGetFeatureByOrganizationQuery,
  // Voice Enrollment hooks
  useGetV2AdminUsersByUserIdVoiceEnrollmentQuery: useGetVoiceEnrollmentStatusQuery,
  useDeleteV2AdminUsersByUserIdVoiceEnrollmentMutation: useResetVoiceEnrollmentMutation,
} = enhancedApi;

export const {
  useUploadImportFileMutation,
  usePresignRawUploadsMutation,
  // Data-migration review: fetch every conflict / patient match to completion
  // (GDN-2198) so large sessions can be fully reviewed and committed.
  useListAllClassifyConflictsQuery,
  useListAllSessionPatientMatchesQuery,
  useRequestClassifyMutation,
  useGetStagedResourceDocumentQuery,
  useBrowseStagedResourcesQuery,
  useSelectImportRowsMutation,
  useClearImportSelectionMutation,
  // Org-review flag toggle (GDN-2300).
  useSetOrgReviewMutation,
  useGetFeaturesListQuery,
  useUpdateProviderFeatureFlagMutation,
  // Streaming threads hooks
  useGetRecentStreamingThreadsQuery,
  useGetStreamingThreadDetailQuery,
  // Prospects (Sales Data) hooks
  useGetProspectsQuery,
  useGetProspectByIdQuery,
  useUploadCsvProspectsMutation,
  useGetBundlesIncludingHiddenQuery,
  useCancelBundleAssignmentMutation,
  // Agent PDF export
  useExportAgentPdfMutation,
  // Eligibility Workqueue hooks
  useGetAdminEligibilityListQuery,
  useAdminOverrideEligibilityMutation,
  // DoseSpot patient management hooks
  useSearchDosespotPatientsQuery,
  useGetDosespotPatientQuery,
  useUpdateDosespotPatientMutation,
  // DoseSpot onboarding status override
  useUpdateDosespotOnboardingStatusMutation,
} = adminV2Api;

// Statsig Gates hooks
export const {
  useGetV2AdminApplicationConfigStatsigGatesQuery: useGetStatsigGatesQuery,
  useGetV2AdminApplicationConfigStatsigUsersByUserIdGatesQuery: useGetUserGatesQuery,
  useGetV2AdminApplicationConfigStatsigGatesByGateIdOverridesQuery: useGetStatsigGateOverridesQuery,
  usePatchV2AdminApplicationConfigStatsigGatesByGateIdOverridesMutation:
    useAddStatsigGateOverrideMutation,
  usePostV2AdminApplicationConfigStatsigGatesByGateIdOverridesRemoveMutation:
    useRemoveStatsigGateOverrideMutation,
  useGetV2AdminApplicationConfigStatsigSystemNotificationsQuery:
    useGetSystemNotificationsConfigQuery,
  usePutV2AdminApplicationConfigStatsigSystemNotificationsMutation:
    useUpdateSystemNotificationsConfigMutation,
  // Org-scoped Statsig gate hooks
  useGetV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGatesQuery: useGetOrgGatesQuery,
  usePatchV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGatesAndGateIdOverridesMutation:
    useSetOrgStatsigGateOverrideMutation,
  usePostV2AdminApplicationConfigStatsigOrganizationsByOrganizationIdGatesAndGateIdOverridesRemoveMutation:
    useRemoveOrgStatsigGateOverrideMutation,
  // Provider profile update
  usePatchV2AdminProvidersByProviderIdProfileMutation: useUpdateProviderProfileMutation,
  // Resource Access registry: catalog + schema + owner-scoped list + detail
  // (GDN-1921/GDN-1928 — hook names come from the routes' operation_ids)
  useListResourceTypesQuery,
  useGetResourceTypeSchemaQuery,
  useListResourcesByTypeQuery,
  useGetResourceDetailQuery,
  // Cortex Policies (GDN-1816)
  useGetV2AdminCortexPoliciesQuery: useListCortexPoliciesQuery,
  useGetV2AdminCortexPoliciesByPolicyIdQuery: useGetCortexPolicyQuery,
  usePostV2AdminCortexPoliciesMutation: useCreateCortexPolicyMutation,
  usePatchV2AdminCortexPoliciesByPolicyIdMutation: useUpdateCortexPolicyMutation,
  usePostV2AdminCortexPoliciesByPolicyIdSupersedeMutation: useSupersedeCortexPolicyMutation,
  usePostV2AdminCortexPoliciesByPolicyIdPromoteMutation: usePromoteCortexPolicyMutation,
  usePostV2AdminCortexPoliciesByPolicyIdDeprecateMutation: useDeprecateCortexPolicyMutation,
  // Policy type registry (typed policies)
  useGetV2AdminCortexPolicyTypesQuery: useListCortexPolicyTypesQuery,
  useGetV2AdminCortexPolicyTypesByPolicyTypeSchemaQuery: useGetCortexPolicyTypeSchemaQuery,
} = enhancedApi;

export default adminV2Api;
